/**
 * Spec 048: «Находки» — the base of findings the company's staff bring from the internet: one
 * thing one card, repeats as mentions. People sort it here: a ring («Берём / Пробуем / Смотрим /
 * Устарело»), trust with what it rests on, «перепроверено», merging a duplicate, rejecting and
 * deleting. «Опробовано» comes from a use only, never from a button. Step 2 (Kostya, 21.10:
 * «Радар» is a department like any other, its findings are knowledge): a page of «Знания»
 * (`/knowledge/radar`), no longer a tab of «Отделы»; `?open=` opens a finding.
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { Radar, type RadarItemOut, type RadarQuery, type RadarRing } from "@/api/radar";
import { Button } from "@/components/ui/Button";
import { TextArea, TextField } from "@/components/ui/Field";
import { Segmented } from "@/components/ui/Segmented";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { ago, AppBar, Caption, EmptyNote, Pill, ScreenHeader } from "@/kit";
import { attention, itemLine, KIND_LABEL, metricsLine, placeLabel, RING_LABEL, ringTone, TRUST_LABEL, trustTone } from "@/lib/radar";
import { useBackButton } from "@/lib/telegram-ui";

type Shelf = "review" | RadarRing | "due" | "fading";

const SHELVES: { value: Shelf; label: string }[] = [
  { value: "review", label: "Разбор" },
  { value: "adopt", label: "Берём" },
  { value: "trial", label: "Пробуем" },
  { value: "assess", label: "Смотрим" },
  { value: "hold", label: "Устарело" },
  { value: "due", label: "Проверить" },
  { value: "fading", label: "Затухает" },
];

function shelfQuery(shelf: Shelf, q: string): RadarQuery {
  const text = q.trim() || undefined;
  if (shelf === "due") return { q: text, due: true };
  if (shelf === "fading") return { q: text, fading: true };
  return { q: text, ring: shelf };
}

export function RadarScreen() {
  useBackButton("/knowledge");
  const [shelf, setShelf] = useState<Shelf>("review");
  const [q, setQ] = useState("");
  const [search, setSearch] = useSearchParams();
  const open = search.get("open");
  const setOpen = (id: string | null) => {
    const next = new URLSearchParams(search);
    if (id) next.set("open", id);
    else next.delete("open");
    setSearch(next, { replace: true });
  };
  const params = shelfQuery(shelf, q);
  const list = useQuery({ queryKey: ["radar", "list", params], queryFn: () => Radar.list(params) });
  const trends = useQuery({ queryKey: ["radar", "trends"], queryFn: () => Radar.trends(7) });

  return (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/knowledge" context="Знания" />
      <ScreenHeader title="Находки" sub="Что сотрудники принесли из интернета" />
      <p className="text-meta text-p-text-muted">
        Репозитории, сервисы, скиллы, MCP, приёмы, визуальные компоненты. Одна вещь — одна карточка, повтор — упоминание. «Берём» — только опробованное.
      </p>
      <TextField label="Поиск" placeholder="название, ключ, слова" value={q} maxLength={200} onChange={(e) => setQ(e.target.value)} />
      <div className="overflow-x-auto">
        <Segmented options={SHELVES} value={shelf} onChange={setShelf} label="Полка" />
      </div>
      {list.isLoading && <SkeletonList count={3} />}
      {list.isError && (
        <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void list.refetch()}>Повторить</Button>}>
          Не удалось загрузить находки.
        </EmptyNote>
      )}
      {list.data && !list.data.length && <EmptyNote>Здесь пока пусто.</EmptyNote>}
      {(list.data ?? []).length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {(list.data ?? []).map((item) => (
            <li key={item.id} className="border-b border-p-border last:border-0">
              <ItemRow item={item} onOpen={() => setOpen(item.id)} />
            </li>
          ))}
        </ul>
      )}
      {(trends.data ?? []).length > 0 && (
        <section className="flex flex-col gap-2" aria-label="Набирает">
          <Caption>Набирает за неделю</Caption>
          <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
            {(trends.data ?? []).slice(0, 10).map((t) => (
              <li key={t.item.id} className="border-b border-p-border last:border-0">
                <button type="button" className="flex w-full flex-col gap-1 px-4 py-3 text-left press-surface" onClick={() => setOpen(t.item.id)}>
                  <span className="text-[15px] font-semibold leading-5 text-p-text">{t.item.title}</span>
                  <span className="text-meta text-p-text-muted">
                    {t.mentions} за неделю · каналов: {t.channels}
                    {metricsLine(t.growth) ? ` · рост: ${metricsLine(t.growth)}` : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {open && <ItemSheet id={open} onClose={() => setOpen(null)} onOpen={setOpen} />}
    </div>
  );
}

function ItemRow({ item, onOpen }: { item: RadarItemOut; onOpen: () => void }) {
  const note = attention(item);
  return (
    <button type="button" className="flex w-full flex-col gap-1 px-4 py-3 text-left press-surface" onClick={onOpen}>
      <span className="text-[15px] font-semibold leading-5 text-p-text">{item.title}</span>
      <span className="text-meta text-p-text-muted">{itemLine(item)}</span>
      <span className="flex flex-wrap items-center gap-2">
        <Pill tone={ringTone(item.ring)}>{placeLabel(item)}</Pill>
        <Pill tone={trustTone(item.trust)}>{TRUST_LABEL[item.trust]}</Pill>
        {note && <Pill tone="needs">{note}</Pill>}
      </span>
    </button>
  );
}

const RINGS: { value: RadarRing; label: string }[] = (Object.keys(RING_LABEL) as RadarRing[]).map((value) => ({ value, label: RING_LABEL[value] }));

function ItemSheet({ id, onClose, onOpen }: { id: string; onClose: () => void; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const detail = useQuery({ queryKey: ["radar", "item", id], queryFn: () => Radar.get(id) });
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [into, setInto] = useState("");
  const item = detail.data;
  const refresh = () => void qc.invalidateQueries({ queryKey: ["radar"] });
  const fail = (e: Error) => {
    toast.error(e.message);
    if (e instanceof ApiError && e.status === 409) refresh();
  };
  const save = useMutation({
    mutationFn: (body: Parameters<typeof Radar.update>[1]) => Radar.update(id, body),
    onSuccess: () => {
      toast.success("Сохранено");
      setNote("");
      setReason("");
      refresh();
    },
    onError: fail,
  });
  const merge = useMutation({
    mutationFn: () => Radar.merge(id, item!.version, into.trim()),
    onSuccess: (kept) => {
      toast.success("Слита");
      refresh();
      onOpen(kept.id);
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => Radar.remove(id),
    onSuccess: () => {
      toast.success("Удалена");
      refresh();
      onClose();
    },
    onError: fail,
  });

  return (
    <Sheet open onClose={onClose} title={item?.title ?? "Находка"}>
      {detail.isLoading && <SkeletonList count={3} />}
      {detail.isError && <EmptyNote>Не удалось загрузить находку.</EmptyNote>}
      {item && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-meta text-p-text-muted">{KIND_LABEL[item.kind]}{item.key ? ` · ${item.key}` : ""}</span>
            <span className="flex flex-wrap items-center gap-2">
              <Pill tone={ringTone(item.ring)}>{placeLabel(item)}</Pill>
              <Pill tone={trustTone(item.trust)}>{TRUST_LABEL[item.trust]}</Pill>
              {attention(item) && <Pill tone="needs">{attention(item)}</Pill>}
            </span>
            {item.summary_md && <p className="whitespace-pre-wrap break-words text-body text-p-text">{item.summary_md}</p>}
            {item.source_url && <a className="break-all text-meta font-semibold text-p-link" href={item.source_url} target="_blank" rel="noreferrer noopener">Первоисточник</a>}
            {item.trust_note && <p className="text-meta text-p-text-muted">Основание: {item.trust_note}</p>}
            {item.reason_md && <p className="text-meta text-p-text-muted">Причина: {item.reason_md}</p>}
            {item.latest_version && <p className="text-meta text-p-text-muted">Версия {item.latest_version}{item.released_at ? ` · ${ago(item.released_at)}` : ""}</p>}
            {item.recheck_by && <p className="text-meta text-p-text-muted">Перепроверить до {new Date(item.recheck_by).toLocaleDateString("ru-RU")}</p>}
            {item.merged_into && (
              <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-link" onClick={() => onOpen(item.merged_into!)}>Открыть ту, в которую слита</button>
            )}
          </div>

          {item.preview_url && <img src={item.preview_url} alt="" className="max-h-64 w-auto self-start rounded-card" loading="lazy" referrerPolicy="no-referrer" />}
          {(item.code_url || item.license || item.case_ok) && (
            <p className="text-meta text-p-text-muted">
              {item.code_url && <a className="font-semibold text-p-link" href={item.code_url} target="_blank" rel="noreferrer noopener">Код</a>}
              {item.license ? ` · лицензия: ${item.license}` : ""}
              {item.case_ok ? ` · в кейс: ${{ yes: "можно", no: "нельзя", ask: "спросить Юриста" }[item.case_ok]}` : ""}
            </p>
          )}

          {item.state !== "merged" && (
            <section className="flex flex-col gap-2">
              <Caption>Кольцо</Caption>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Кольцо">
                {RINGS.map((r) => (
                  <Button key={r.value} size="compact" variant={item.ring === r.value ? "strong" : "secondary"}
                    disabled={item.ring === r.value || save.isPending || (r.value === "adopt" && item.trust !== "tried")}
                    title={r.value === "adopt" && item.trust !== "tried" ? "Сначала опробовать: запись применения" : undefined}
                    onClick={() => {
                      if (r.value === "hold" && !reason.trim()) {
                        toast.error("«Устарело» — с причиной: напиши её ниже");
                        return;
                      }
                      save.mutate({ version: item.version, ring: r.value, ...(r.value === "hold" ? { reason_md: reason.trim() } : {}), ...(item.state === "rejected" ? { restore: true } : {}) });
                    }}>
                    {r.label}
                  </Button>
                ))}
              </div>
              {item.ring === null && item.state === "review" && <p className="text-meta text-p-text-muted">На разборе: выбери кольцо. «Берём» — только когда опробовано.</p>}
              <TextArea label="Подтверждено — что проверил" hint="Открыл первоисточник и второй источник" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="flex flex-wrap gap-2">
                <Button size="compact" variant="secondary" disabled={!note.trim()} loading={save.isPending}
                  onClick={() => save.mutate({ version: item.version, trust: "confirmed", trust_note: note.trim() })}>Подтверждено</Button>
                {item.state === "listed" && (
                  <Button size="compact" variant="secondary" loading={save.isPending} onClick={() => save.mutate({ version: item.version, checked: true })}>Перепроверено</Button>
                )}
              </div>
              <TextArea label="Причина" hint="Для «Устарело» и «Отклонить»" rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
              <div className="flex flex-wrap gap-2">
                {item.state === "rejected" ? (
                  <Button size="compact" variant="secondary" loading={save.isPending} onClick={() => save.mutate({ version: item.version, restore: true })}>Вернуть в разбор</Button>
                ) : (
                  <Button size="compact" variant="destructive-outline" disabled={!reason.trim()} loading={save.isPending}
                    onClick={() => save.mutate({ version: item.version, reject: true, reason_md: reason.trim() })}>Отклонить</Button>
                )}
              </div>
              <TextField label="Это дубль — слить в находку" placeholder="id находки, которая остаётся" value={into} onChange={(e) => setInto(e.target.value)} />
              <div>
                <Button size="compact" variant="secondary" disabled={!/^[0-9a-f-]{36}$/i.test(into.trim())} loading={merge.isPending} onClick={() => merge.mutate()}>Слить</Button>
              </div>
            </section>
          )}

          <section className="flex flex-col gap-2">
            <Caption>Упоминания{item.channels.length ? ` · ${item.channels.join(", ")}` : ""}</Caption>
            {!item.recent_mentions.length && <p className="text-meta text-p-text-muted">Упоминаний нет.</p>}
            <ul className="flex flex-col gap-2">
              {item.recent_mentions.map((m) => (
                <li key={m.id} className="flex flex-col gap-0.5 rounded-card border border-p-border px-3 py-2">
                  <span className="text-meta text-p-text-muted">{m.channel}{m.author ? ` · ${m.author}` : ""} · {ago(m.seen_at)}{m.by ? ` · ${m.by.name}` : ""}</span>
                  {m.note && <span className="break-words text-body text-p-text">{m.note}</span>}
                  {metricsLine(m.metrics) && <span className="text-meta text-p-text-muted">{metricsLine(m.metrics)}</span>}
                  <a className="break-all text-meta font-semibold text-p-link" href={m.url} target="_blank" rel="noreferrer noopener">Открыть пост</a>
                </li>
              ))}
            </ul>
          </section>

          {(item.versions.length > 0 || item.uses.length > 0) && (
            <section className="flex flex-col gap-1">
              <Caption>Версии и применения</Caption>
              {item.versions.map((v) => (
                <span key={v.version} className="text-meta text-p-text-muted">{v.version}{v.released_at ? ` · ${new Date(v.released_at).toLocaleDateString("ru-RU")}` : ""}</span>
              ))}
              {item.uses.map((u) => (
                <span key={u.id} className="text-meta text-p-text">
                  {{ success: "получилось", partial: "частично", failed: "не вышло" }[u.outcome]}
                  {u.task_id ? " · задача" : u.case_id ? " · кейс" : " · поручение"}
                  {u.note ? ` — ${u.note}` : ""}
                </span>
              ))}
            </section>
          )}

          <p className="text-micro text-p-text-muted">Принёс: {item.created_by.name} · {ago(item.created_at)} · id {item.id}</p>
          <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-blocked" disabled={remove.isPending}
            onClick={() => { if (window.confirm("Удалить находку со всеми упоминаниями?")) remove.mutate(); }}>
            Удалить
          </button>
        </div>
      )}
    </Sheet>
  );
}
